import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const styles = {
    carouselDot: {
        height: '8px',
        width: '8px',
        backgroundColor: '#E5E7EB',
        borderRadius: '50%',
        display: 'inline-block',
        margin: '0 4px',
    },
    carouselDotActive: {
        backgroundColor: '#FFFFFF',
    },
    navButton: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        backgroundColor: 'rgba(0, 0, 0, 0)',
        color: '#FFFFFF',
        padding: '10px',
        borderRadius: '50%',
        cursor: 'pointer',
        transition: 'background-color 0.3s',
        zIndex: 10,
    },
    navButtonHover: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
};

const AdminPackageCard = ({ pkg, onEdit, onDelete }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isHovering, setIsHovering] = useState(false);
    const navigate = useNavigate();

    const handleNextImage = () => {
        setCurrentImageIndex((prevIndex) =>
            prevIndex === pkg.images.length - 1 ? 0 : prevIndex + 1
        );
    };

    const handlePrevImage = () => {
        setCurrentImageIndex((prevIndex) =>
            prevIndex === 0 ? pkg.images.length - 1 : prevIndex - 1
        );
    };

    const formatDaysNights = (str) => {
        if (!str || typeof str !== 'string') return 'N/A';
        const match = str.match(/(\d+)D\/(\d+)N/);
        if (!match) return str;
        const [, days, nights] = match;
        return `${parseInt(days)} days & ${parseInt(nights)} nights`;
    };
    const price = typeof pkg.price === 'number' ? pkg.price : 0;
    const originalPrice = typeof pkg.originalPrice === 'number' ? pkg.originalPrice : 0;
    const savings = originalPrice - price;

    return (
        <div
            className="relative m-2 flex-shrink-0 w-72 rounded-lg overflow-hidden shadow-md"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            <div className="relative overflow-hidden h-48 w-full">
                <div
                    className="flex h-48 transition-transform duration-500 ease-in-out"
                    style={{
                        transform: `translateX(-${currentImageIndex * (100 / pkg.images.length)}%)`,
                        width: `${pkg.images.length * 100}%`,
                    }}
                >
                    {pkg.images.map((imgSrc, index) => (
                        <img
                            key={index}
                            src={imgSrc}
                            alt={pkg.title}
                            loading="lazy"
                            className="h-48 object-cover flex-shrink-0"
                            style={{ width: `${100 / pkg.images.length}%` }}
                        />
                    ))}
                </div>

                {isHovering && (
                    <>
                        <button
                            style={{ ...styles.navButton, left: '10px' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = styles.navButtonHover.backgroundColor}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = styles.navButton.backgroundColor}
                            onClick={handlePrevImage}
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <button
                            style={{ ...styles.navButton, right: '10px' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = styles.navButtonHover.backgroundColor}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = styles.navButton.backgroundColor}
                            onClick={handleNextImage}
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </>
                )}

                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2">
                    {pkg.images.map((_, index) => (
                        <span
                            key={index}
                            style={{
                                ...styles.carouselDot,
                                ...(index === currentImageIndex ? styles.carouselDotActive : {}),
                            }}
                        ></span>
                    ))}
                </div>
            </div>

            <div className="p-4 bg-custom-white">
                <p className="text-sm text-gray-600">{formatDaysNights(pkg.days)}</p>
                <div className="flex items-center mt-1">
                    <svg className="w-4 h-4 text-custom-yellow mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span className="text-sm text-gray-600">{pkg.rating} ({pkg.reviews})</span>
                </div>
                <h3 className="mt-2 text-sm font-semibold text-custom-blue line-clamp-2 h-10">{pkg.title}</h3>
                <p className="text-xs text-gray-500 mt-1 line-clamp-1">{pkg.itinerary}</p>
                <div className="flex justify-between items-center mt-2">
                    <div>
                        <span className="text-sm font-bold text-custom-blue">
                            INR {price.toLocaleString()}
                        </span>
                        <span className="text-xs text-gray-500 line-through ml-2">
                            INR {originalPrice.toLocaleString()}
                        </span>
                        <span className="text-xs text-custom-yellow ml-2">
                            SAVE INR {savings.toLocaleString()}
                        </span>
                    </div>
                </div>

                {/* Admin Buttons */}
                <div className="grid grid-cols-2 gap-3 mt-3">
                    <button
                        onClick={() => navigate(`/edit-package/${pkg._id}`)}
                        className="w-full bg-custom-yellow text-custom-blue py-2 rounded-lg flex items-center justify-center hover:bg-custom-blue hover:text-white"
                    >
                        ✏️ Edit
                    </button>

                    <button
                        onClick={() => {
                            if (window.confirm('Are you sure you want to delete this package?')) {
                                onDelete(pkg._id);
                            }
                        }}
                        className="w-full bg-red-500 text-white py-2 rounded-lg flex items-center justify-center hover:bg-red-700"
                    >
                        🗑️ Delete
                    </button>

                </div>
            </div>
        </div>
    );
};

export default AdminPackageCard;
